import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { LogIn, Loader2, Shield } from "lucide-react";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const { signIn, signUp } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let result;
      if (isSignUpMode) {
        result = await signUp(email, password);
        if (!result.error) {
          toast({
            title: "Account Created",
            description: "Account created successfully! You can now sign in.",
          });
          setIsSignUpMode(false);
          return;
        }
      } else {
        result = await signIn(email, password);
      }

      if (result.error) {
        toast({
          title: isSignUpMode ? "Signup Failed" : "Login Failed",
          description: result.error.message || "Invalid credentials",
          variant: "destructive",
        });
      } else if (!isSignUpMode) {
        toast({
          title: "Login Successful",
          description: "Welcome to the admin panel!",
        });
      }
    } catch (error) {
      toast({
        title: isSignUpMode ? "Signup Error" : "Login Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-8">
      <div className="container mx-auto px-4 max-w-md">
        <Card className="bg-gradient-card border-border shadow-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Shield className="h-12 w-12 text-trading-primary" />
            </div>
            <CardTitle className="text-2xl">
              {isSignUpMode ? "Create Admin Account" : "Admin Access"}
            </CardTitle>
            <CardDescription>
              {isSignUpMode 
                ? "Create your admin account to manage the blog system"
                : "Sign in to access the AI blog management system"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@futures-trader-hub.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full"
                variant="trading"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isSignUpMode ? "Creating Account..." : "Signing In..."}
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    {isSignUpMode ? "Create Account" : "Sign In"}
                  </>
                )}
              </Button>
              
              <div className="text-center mt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsSignUpMode(!isSignUpMode)}
                  className="text-sm"
                >
                  {isSignUpMode 
                    ? "Already have an account? Sign in" 
                    : "Need an account? Sign up"
                  }
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default AdminLogin;